import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const exerciseSchema = z.object({
  exercise_name: z.string().min(1, "Nome do exercício é obrigatório"),
  equipment: z.string().optional(),
  sets: z.coerce.number().min(1).max(10).optional(),
  reps: z.string().optional(),
  springs: z.string().optional(),
  focus_area: z.enum(["core", "legs", "arms", "back", "stretch"]).optional(),
  difficulty: z.enum(["beginner", "intermediate", "advanced"]),
  notes: z.string().optional(),
});

type ExerciseFormData = z.infer<typeof exerciseSchema>;

interface Exercise {
  id: string;
  exercise_name: string;
  equipment: string | null;
  sets: number | null;
  reps: string | null;
  springs: string | null;
  focus_area: string | null;
  difficulty: string;
  notes: string | null;
}

interface ExerciseEditModalProps {
  exercise: Exercise | null;
  open: boolean;
  onClose: () => void;
  onSave: (exerciseId: string, data: ExerciseFormData) => Promise<void>;
}

export default function ExerciseEditModal({
  exercise,
  open,
  onClose,
  onSave,
}: ExerciseEditModalProps) {
  const form = useForm<ExerciseFormData>({
    resolver: zodResolver(exerciseSchema),
    defaultValues: {
      exercise_name: exercise?.exercise_name || "",
      equipment: exercise?.equipment || "",
      sets: exercise?.sets || undefined,
      reps: exercise?.reps || "",
      springs: exercise?.springs || "",
      focus_area: (exercise?.focus_area as any) || undefined,
      difficulty: (exercise?.difficulty as any) || "intermediate",
      notes: exercise?.notes || "",
    },
  });

  const handleSubmit = async (data: ExerciseFormData) => {
    if (!exercise) return;
    await onSave(exercise.id, data);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Exercício</DialogTitle>
          <DialogDescription>
            Modifique os detalhes do exercício abaixo
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="exercise_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Exercício *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Roll Up" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="equipment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Equipamento</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Reformer" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sets"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Séries</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} max={10} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="reps"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Repetições</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: 8-10" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="springs"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Molas</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: 2 vermelhas" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="focus_area"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Área de Foco</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="core">Core</SelectItem>
                        <SelectItem value="legs">Pernas</SelectItem>
                        <SelectItem value="arms">Braços</SelectItem>
                        <SelectItem value="back">Costas</SelectItem>
                        <SelectItem value="stretch">Alongamento</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="difficulty"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dificuldade *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="beginner">Iniciante</SelectItem>
                        <SelectItem value="intermediate">Intermediário</SelectItem>
                        <SelectItem value="advanced">Avançado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Detalhes adicionais sobre o exercício..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-pink-500 to-rose-500">
                Salvar Alterações
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
